import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, tap, throwError } from 'rxjs';
import { ErrorVTO, NewRecordVTO } from '../../pages/admin-dashboard/products/product.model';
import { AuthService } from '../auth-service';
import {
  CategoryDTO,
  CategoryResultSet,
  CategoryVTO,
  SubcategoryResultSet,
} from '../../pages/admin-dashboard/categories/category.model';
import { CurrentUserService } from '../current-user.service';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class CategoryService {
  private router = inject(Router);
  private currentUserService = inject(CurrentUserService);
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private API = environment.productsApiUrl;

  getAllCategories(paramsIn?: {
    categoryTitle?: string;
    pageSize?: number;
    pageOffset?: number;
    pageNum?: number;
    orderBy?: 'CREATION_DATE' | 'CATEGORY_TITLE';
    orderDir?: 'ASC' | 'DESC';
  }) {
    let params = new HttpParams();
    if (paramsIn) {
      if (paramsIn.categoryTitle) {
        params = params.set('categoryTitle', paramsIn.categoryTitle);
      }
      if (paramsIn.pageSize) {
        params = params.set('pageSize', paramsIn.pageSize.toString());
      }
      if (paramsIn.pageOffset) {
        params = params.set('pageOffset', paramsIn.pageOffset.toString());
      }
      if (paramsIn.pageNum) {
        params = params.set('pageNum', paramsIn.pageNum.toString());
      }
      if (paramsIn.orderBy) {
        params = params.set('orderBy', paramsIn.orderBy);
      }
      if (paramsIn.orderDir) {
        params = params.set('orderDir', paramsIn.orderDir);
      }
    }
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.get<CategoryResultSet>(`${this.API}/categories`, { params, headers }).pipe(
      tap(() => console.log('Categories retrieved successfully')),
      catchError(this.handleError),
    );
  }

  createCategory(dto: CategoryDTO) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.post<NewRecordVTO>(`${this.API}/categories`, dto, { headers }).pipe(
      tap(() => console.log('Category created successfully')),
      catchError(this.handleError),
    );
  }

  getCategoryById(categoryId: number) {
    let headers = new HttpHeaders();
    // headers = headers.set('X-No-Cache', 'true');
    const token = this.authService.getAccessToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.get<CategoryVTO>(`${this.API}/categories/${categoryId}`, { headers }).pipe(
      tap(() => console.log('Category retrieved successfully')),
      catchError(this.handleError),
    );
  }

  updateCategory(categoryId: number, dto: CategoryDTO) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.put<void>(`${this.API}/categories/${categoryId}`, dto, { headers }).pipe(
      tap(() => console.log('Category updated successfully')),
      catchError(this.handleError),
    );
  }

  deleteCategory(categoryId: number) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.delete<void>(`${this.API}/categories/${categoryId}`, { headers }).pipe(
      tap(() => console.log('Category deleted successfully')),
      catchError(this.handleError),
    );
  }

  getSubcategories(paramsIn: { categoryId: number; orderBy?: string; orderDir?: 'ASC' | 'DESC' }) {
    let params = new HttpParams()
      .set('categoryId', paramsIn.categoryId.toString())
      .set('orderBy', paramsIn.orderBy || 'CREATION_DATE');

    if (paramsIn.orderDir) {
      params = params.set('orderDir', paramsIn.orderDir);
    }

    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.get<SubcategoryResultSet>(`${this.API}/subcategories`, { params, headers }).pipe(
      tap(() => console.log(`Subcategories for category ${paramsIn.categoryId} retrieved`)),
      catchError(this.handleError),
    );
  }

  private handleError(error: HttpErrorResponse) {
    // if (error.status === 403) {
    //   this.router.navigate(['/login']);
    //   this.currentUserService.logout();
    // }
    const apiError: ErrorVTO = error.error;

    return throwError(() => apiError);
  }
}
